'use client';

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@tranquility-ai/theme-components/button';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import {
  CaretRightIcon,
  CheckIcon,
  FileIcon,
  FolderOpenIcon,
  HouseIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { FolderItem, TreeNodeResponse } from '@/shared/cors-provenance-api';
import {
  useProvenanceFolders,
  useProvenanceTree,
  useProvenanceTreeSearch,
} from '@/hooks/useProvenanceTree';
import {
  iconForTreeNode as iconForFileNode,
  pathTail,
  pickCaseRootDirectoryNode,
  treeNodeMeta as nodeMeta,
} from '@/shared/provenance-tree-ui';
import { PATH_SEGMENT_TRUNCATE_CLASSNAME } from '@/shared/uiConstants';

/**
 * Public ref API: lets the parent breadcrumb back to a particular node when
 * the user clicks one of the picked-file chips below the chat bar. Mirrors
 * the zprovenance ``TreeBrowserHandle``.
 */
export interface EvidenceLockerTreeHandle {
  /** Navigate the tree so the given node is in view (and clear search). */
  navigateToNode: (node: TreeNodeResponse) => void;
  /**
   * Return the URLs for every currently-displayed row, split by kind. Used by
   * the parent's "Select All" handler so it can populate
   * ``selectedEvidence`` / ``selectedScopes`` with exactly what the user sees
   * (rather than the legacy flat ``availableEvidence`` list, which is unaware
   * of folder navigation and subdocuments).
   */
  getVisibleSelection: () => {
    fileUrls: string[];
    folderUrls: string[];
  };
}

interface NavEntry {
  name: string;
  path: string;
  fileStatusId: string | null;
  nodeType: TreeNodeResponse['nodeType'];
}

const ROW_HEIGHT = 40;
const ROW_GAP = 2;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function nodeFileStatusId(node: TreeNodeResponse): string | null {
  return node.fileStatusId ?? (node.nodeType === 'file' ? (node.id ?? null) : null);
}

/**
 * Build the absolute searchable path for a node. The assistant-service indexes
 * chunks under ``{source.searchable_path}/{relative path inside source}`` (see
 * tai-processing ``TurbopufferDocument._absolute_searchable_path``), so picker
 * URLs that drive the Glob filter must match that shape:
 *
 *   source_file.searchable_path = ``MBOX/Manual/evidence_package.mbox``
 *   subdoc.path (relative)      = ``email_1/attachment_2/financial_records.xlsx``
 *   → absolute path             = ``MBOX/Manual/evidence_package.mbox/email_1/attachment_2/financial_records.xlsx``
 *
 * Top-level files / root-level directories already carry an absolute
 * ``node.path`` (= ``location.searchable_path``) so we leave them alone.
 * Subdocs from ``/v1/tree/search`` carry ``sourceFile`` directly. Subdocs and
 * directories returned from the browse path don't (the API only knows their
 * relative position inside the parent file), so callers pass
 * ``contextSourceFilePath`` derived from the breadcrumb's most-recent
 * file-type entry.
 */
function nodeAbsolutePath(
  node: TreeNodeResponse,
  contextSourceFilePath?: string,
): string {
  const sourcePath =
    node.sourceFile?.searchablePath ?? contextSourceFilePath ?? '';
  if (!sourcePath) return node.path;
  return `${sourcePath}/${node.path}`;
}

function nodePickUrl(
  node: TreeNodeResponse,
  toUrl: (path: string) => string,
  contextSourceFilePath?: string,
): string {
  if (node.nodeType === 'subdocument') {
    return toUrl(nodeAbsolutePath(node, contextSourceFilePath));
  }
  return toUrl(node.path);
}

/**
 * URL used for the SourceCard preview popover. Subdocuments carry the actual
 * processed-S3 URI in ``fileInfo.subdocument.fileUrl``; anything else falls
 * back to the scope/pick URL so SourceCard's existing resolver path keeps
 * working.
 */
function nodePreviewUrl(node: TreeNodeResponse, fallbackUrl: string): string {
  if (node.nodeType === 'subdocument') {
    const { fileUrl } = nodeMeta(node);
    if (typeof fileUrl === 'string' && fileUrl.length > 0) return fileUrl;
  }
  return fallbackUrl;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EvidenceLockerTreeProps {
  /**
   * - ``'tree'`` (default) — hierarchical browse via ``/v1/tree`` + search via
   *   ``/v1/tree/search``. Breadcrumb navigation, drill-in carets, file
   *   previews, subdocument support.
   * - ``'folders'`` — flat list of every folder anywhere in the cell via
   *   ``/v1/folders``. No breadcrumb, no drill-in, no file rows. Used by
   *   the EvidenceLocker's "Folders" filter chip when the user wants to
   *   scope by folder without descending the tree.
   */
  mode?: 'tree' | 'folders';
  /** Debounced search input (only sent upstream when length > 2). */
  searchQuery: string;
  /** Content category tokens, e.g. ``['pdf','image']``. */
  contentCategories?: string[];
  /** Optional case tag → forwarded as ``tags`` filter. */
  caseTag?: string | null;
  /**
   * A specific case is selected but its tag has not resolved. Every query stays
   * off: an untagged request is a whole-tenant listing, not this case's tree.
   */
  caseScopeUnresolved?: boolean;
  /**
   * When ``caseTag`` is set, used to pick which top-level directory is the case
   * root among siblings (name/path/tail match). Usually the selected case id
   * or display name from the parent.
   */
  scopeCaseFolderMatchId?: string | null;
  /** S3-style URLs currently in ``selectedEvidence`` (pending include). */
  pickedPaths: Set<string>;
  /** S3-style URLs currently in ``excludedEvidence`` (pending exclude). */
  excludedPaths?: Set<string>;
  /** Folder paths currently in ``selectedScopes`` (pending include). */
  pickedFolderPaths?: Set<string>;
  /** Folder paths currently in ``excludedScopes`` (pending exclude). */
  excludedFolderPaths?: Set<string>;
  /**
   * Toggle pick on a file row. Receives the canonicalized lookup path
   * (``toProvenanceLookupPath(node.path)``) that mirrors what the legacy flat
   * list pushed into ``selectedEvidence``.
   */
  onTogglePick: (url: string, node: TreeNodeResponse) => void;
  /**
   * Toggle pick on a directory row. Receives the canonicalized folder path
   * (``toProvenanceLookupPath(node.path)``). Wires to ``selectedScopes`` in
   * the parent so the chat-submit payload sends folder scopes alongside file
   * picks. If not provided, directory rows are not pickable.
   */
  onTogglePickFolder?: (url: string, node: TreeNodeResponse) => void;
  /**
   * Render the preview-icon + Popover for a file row. The render prop receives
   * the canonical URL so the parent can look up the matching
   * ``availableEvidence`` index, anchor its existing ``Popover``, and reuse
   * the legacy SourceCard preview wiring untouched.
   */
  renderRowPreview?: (params: {
    url: string;
    node: TreeNodeResponse;
    mediaType: string;
    pageCount: number | null;
  }) => React.ReactNode;
  /**
   * Optional content rendered immediately to the right of the filename in each
   * file row (e.g. content-moderation category pills). Receives the same params
   * as ``renderRowPreview`` so the parent can look up per-row metadata.
   */
  renderRowNameSuffix?: (params: {
    url: string;
    node: TreeNodeResponse;
    mediaType: string;
    pageCount: number | null;
  }) => React.ReactNode;
  /** Toggle to clear search on the parent (e.g. when navigating into a folder). */
  onClearSearch?: () => void;
  /**
   * Normalize a tree-node ``path`` into the canonical evidence URL. The
   * parent passes the existing ``toProvenanceLookupPath`` helper so the
   * picked URLs match the rest of the question-bar state.
   */
  toUrl: (path: string) => string;
  /**
   * Slot rendered on the right side of the breadcrumb / results-header row.
   * Used by the parent to anchor Select All / Clear All next to the actual
   * view they affect, instead of floating in an unrelated header row above.
   */
  headerActions?: React.ReactNode;
  ref?: React.Ref<EvidenceLockerTreeHandle>;
}

/**
 * Light-themed tree picker for the EvidenceLocker. Drops in where the legacy
 * flat ``availableEvidence`` list rendered, but exposes the same pick
 * semantics so ``selectedEvidence``/``scopesAndFiles.files`` keep flowing
 * unchanged.
 *
 * Modes:
 * - **Browse**: ``searchQuery.length <= 2`` AND no ``contentCategories`` → uses
 *   ``GET /tree`` with breadcrumb-driven path navigation and infinite scroll.
 * - **Search**: 3+ char query OR any category selected → uses
 *   ``GET /tree/search`` (flat results across the cell).
 *
 * Subdocument nodes are filtered out in v1 to keep the picked URL contract
 * identical to the flat list (one URL per ingested source file). They can
 * be re-introduced once subdocument-level chat scoping ships.
 */
export default function EvidenceLockerTree({
  mode = 'tree',
  searchQuery,
  contentCategories,
  caseTag,
  caseScopeUnresolved = false,
  scopeCaseFolderMatchId,
  pickedPaths,
  excludedPaths,
  pickedFolderPaths,
  excludedFolderPaths,
  onTogglePick,
  onTogglePickFolder,
  renderRowPreview,
  renderRowNameSuffix,
  onClearSearch,
  toUrl,
  headerActions,
  ref,
}: EvidenceLockerTreeProps) {
  const t = useTimePilotT();
  const [navStack, setNavStack] = useState<NavEntry[]>([]);
  /** Number of leading ``navStack`` entries hidden in the breadcrumb (the case folder treated as root). */
  const [caseBrowseRootCrumbDepth, setCaseBrowseRootCrumbDepth] = useState(0);
  const isFoldersMode = mode === 'folders';

  // In folders mode there's no hierarchy navigation — clear any nav state and
  // ignore content-category filters (the /folders endpoint doesn't accept
  // them; the parent only renders folders-mode when no file-type chips are
  // active anyway).
  useEffect(() => {
    if (!isFoldersMode) return;
    setNavStack([]);
    setCaseBrowseRootCrumbDepth(0);
  }, [isFoldersMode]);

  useEffect(() => {
    setNavStack([]);
    setCaseBrowseRootCrumbDepth(0);
  }, [caseTag]);

  const hasTextSearch = !!(searchQuery && searchQuery.trim().length > 2);
  const hasCategoryFilter = !!(contentCategories && contentCategories.length > 0);
  const currentEntry = navStack[navStack.length - 1];
  // ``/tree/search`` cannot take ``fileStatusId``. Category-filtered search hits
  // for archives (zips tagged ``image``/etc.) must switch to browse when the user
  // drills into the container — otherwise the same flat search payload is
  // re-shown and the breadcrumb appends the zip name forever.
  const insideContainer = !!currentEntry?.fileStatusId;
  const isSearching =
    !isFoldersMode && (hasTextSearch || (hasCategoryFilter && !insideContainer));

  // The most-recent ``file``-type breadcrumb entry's ``path`` is the absolute
  // ``searchablePath`` of the source file the user drilled into (e.g.
  // ``MBOX/Manual/evidence_package.mbox``). Subdocuments and virtual
  // directories returned by the browse path are reported relative to that
  // file, so we need this prefix to reconstruct their absolute searchable
  // path for picker URLs. ``undefined`` when not inside any file.
  const contextSourceFilePath = useMemo(() => {
    for (let i = navStack.length - 1; i >= 0; i--) {
      if (navStack[i].nodeType === 'file') return navStack[i].path;
    }
    return undefined;
  }, [navStack]);
  const tags = caseTag ? [caseTag] : undefined;
  const tagMatch = caseTag ? ('any' as const) : undefined;

  // ``path`` + ``fileStatusId`` interplay (mirrors zprovenance NodeStatusTreeView):
  //   - No nav entry                       → list root  (no path, no fsid)
  //   - Directory entry, no fsid           → list path  (path only)
  //   - File entry (fsid set)              → list file's direct children (fsid only)
  //   - Anything-else-inside-a-file (fsid + nodeType !== 'file')
  //                                         → BOTH fsid AND path so the backend
  //                                           scopes within that file. This covers
  //                                           both subdocuments AND virtual
  //                                           directories that exist inside a file
  //                                           (e.g. mbox folders).
  const treeParams = currentEntry
    ? currentEntry.fileStatusId
      ? {
          fileStatusId: currentEntry.fileStatusId,
          path: currentEntry.nodeType === 'file' ? undefined : currentEntry.path,
        }
      : { path: currentEntry.path, fileStatusId: undefined }
    : { path: undefined, fileStatusId: undefined };

  const treeQuery = useProvenanceTree({
    path: treeParams.path,
    fileStatusId: treeParams.fileStatusId,
    view: 'user',
    tags,
    tagMatch,
    // Keep Image/Audio/… filters active while browsing inside a container.
    contentCategories: insideContainer ? contentCategories : undefined,
    enabled: !caseScopeUnresolved && !isSearching && !isFoldersMode,
  });

  // Folders mode: ``GET /v1/folders`` returns every directory anywhere in
  // the cell, with optional ``searchQuery`` substring filter. We synthesize
  // ``TreeNodeResponse`` directories from the API payload so the existing
  // ``TreeRow`` rendering path (pick checkbox, hover styling, virtualization,
  // ``getVisibleSelection``) stays untouched. ``hasChildren`` is forced to
  // ``false`` so no drill-in caret renders — folders mode is intentionally
  // flat.
  const foldersSearch =
    searchQuery && searchQuery.trim().length > 2 ? searchQuery.trim() : undefined;
  const foldersQuery = useProvenanceFolders({
    searchQuery: foldersSearch,
    tags,
    tagMatch,
    enabled: !caseScopeUnresolved && isFoldersMode,
  });
  // ``enabled: false`` stops fetching but not reading: with no ``caseTag`` these
  // hooks share the untagged All Cases key, so a previously cached cell-wide
  // listing would still render. Read nothing at all until the case resolves.
  const folderItems: FolderItem[] = useMemo(
    () =>
      caseScopeUnresolved
        ? []
        : (foldersQuery.data?.pages.flatMap((p) => p.items) ?? []),
    [foldersQuery.data, caseScopeUnresolved],
  );
  const folderNodes = useMemo<TreeNodeResponse[]>(
    () =>
      // Render the *full* path (e.g. "Enron/Manual/E-Mails") as the row
      // label since folders mode is flat — without ancestors, a row labelled
      // just "E-Mails" would be ambiguous between every folder of that name
      // in the cell. ``path`` stays the canonical pick URL.
      folderItems.map((f) => ({
        nodeType: 'directory' as const,
        name: f.path,
        path: f.path,
        hasChildren: false,
      })),
    [folderItems],
  );

  // ``q`` selection for search mode:
  //   - User-typed query wins.
  //   - Otherwise fall back to the current nav-stack folder path so a
  //     content-category filter (Audio / Video / …) combined with breadcrumb
  //     drill-in stays scoped to the folder the user clicked into.
  //
  // The backend's ``/tree/search`` uses ILIKE substring match on
  // ``searchablePath``, so passing the folder path as ``q`` effectively
  // restricts category-filtered results to that subtree.
  //
  // Only do the path-fallback for plain directory entries — drilling into a
  // file or subdocument relies on ``fileStatusId`` (which ``/tree/search``
  // doesn't accept) so we skip the search in those cases.
  const userQuery = searchQuery && searchQuery.trim().length > 2 ? searchQuery.trim() : undefined;
  const navPathQuery =
    !userQuery && currentEntry && !currentEntry.fileStatusId ? currentEntry.path : undefined;
  const searchTerm = userQuery ?? navPathQuery;

  const searchQueryHook = useProvenanceTreeSearch({
    q: searchTerm,
    contentCategories,
    view: 'user',
    tags,
    tagMatch,
    enabled: !caseScopeUnresolved && isSearching,
  });

  const browseItems = useMemo<TreeNodeResponse[]>(
    () =>
      caseScopeUnresolved
        ? []
        : (treeQuery.data?.pages.flatMap((p) => p.items) ?? []),
    [treeQuery.data, caseScopeUnresolved],
  );
  const searchItems = caseScopeUnresolved ? [] : (searchQueryHook.data ?? []);

  // With a case tag, the tree root is often a single case folder — drill in once
  // so the locker lists that folder's children and the breadcrumb stays at "Root".
  useEffect(() => {
    if (!caseTag || isFoldersMode || isSearching) return;
    if (navStack.length > 0) return;
    if (treeQuery.isLoading || browseItems.length === 0) return;
    const caseDir = pickCaseRootDirectoryNode(browseItems, caseTag, scopeCaseFolderMatchId);
    if (!caseDir) return;
    setNavStack([
      { name: caseDir.name, path: caseDir.path, fileStatusId: null, nodeType: 'directory' },
    ]);
    setCaseBrowseRootCrumbDepth(1);
  }, [
    caseTag,
    scopeCaseFolderMatchId,
    isFoldersMode,
    isSearching,
    browseItems,
    treeQuery.isLoading,
    navStack.length,
  ]);

  // Show all node kinds. Files / subdocuments with ``has_children`` get a
  // drill-in affordance so users can navigate into multi-document containers
  // (mbox files, archives, PDFs with subdocuments, …).
  const displayItems = useMemo(() => {
    if (isFoldersMode) return folderNodes;
    return isSearching ? searchItems : browseItems;
  }, [isFoldersMode, folderNodes, isSearching, searchItems, browseItems]);

  const browseTotal = caseScopeUnresolved ? 0 : (treeQuery.data?.pages?.[0]?.total ?? 0);
  // ``fetchNextPage`` is imperative and ignores ``enabled``, so an untagged
  // "load more" has to be unreachable rather than merely disabled.
  const browseHasMore = !caseScopeUnresolved && treeQuery.hasNextPage === true;
  const browseFetchingMore = treeQuery.isFetchingNextPage;
  const fetchNextTreePage = treeQuery.fetchNextPage;
  const foldersTotal = caseScopeUnresolved
    ? 0
    : (foldersQuery.data?.pages?.[0]?.total ?? folderNodes.length);
  const foldersHasMore = !caseScopeUnresolved && foldersQuery.hasNextPage === true;
  const foldersFetchingMore = foldersQuery.isFetchingNextPage;
  const fetchNextFoldersPage = foldersQuery.fetchNextPage;

  // Unify the paging surface so the virtualizer/sentinel logic below stays
  // mode-agnostic. ``loadMore`` is the only handle the JSX below needs.
  const paginatorHasMore = isFoldersMode ? foldersHasMore : !isSearching && browseHasMore;
  const paginatorFetchingMore = isFoldersMode
    ? foldersFetchingMore
    : !isSearching && browseFetchingMore;
  const loadMore = isFoldersMode ? fetchNextFoldersPage : fetchNextTreePage;

  const loading = isFoldersMode
    ? foldersQuery.isLoading
    : isSearching
      ? searchQueryHook.isLoading
      : treeQuery.isLoading;
  const error = isFoldersMode
    ? foldersQuery.error
    : isSearching
      ? searchQueryHook.error
      : treeQuery.error;
  const showEmpty = !loading && !error && displayItems.length === 0;

  const showLoadMore = paginatorHasMore && !loading;
  const showLoadingMore = paginatorFetchingMore && displayItems.length > 0;
  const hasExtraRow = showLoadMore || showLoadingMore;

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: displayItems.length + (hasExtraRow ? 1 : 0),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    gap: ROW_GAP,
    overscan: 10,
  });

  // Infinite-scroll sentinel — covers both tree-browse and folders-mode
  // pagination. Search responses come back as a single flat array so they
  // intentionally skip this.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    if (!paginatorHasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !paginatorFetchingMore) {
            loadMore();
          }
        }
      },
      { root, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [paginatorHasMore, paginatorFetchingMore, loadMore]);

  const navigateInto = useCallback((node: TreeNodeResponse) => {
    setNavStack((prev) => {
      const parent = prev[prev.length - 1];
      let fsId: string | null = null;
      if (node.nodeType === 'file') {
        fsId = nodeFileStatusId(node);
      } else if (node.fileStatusId) {
        fsId = node.fileStatusId;
      } else if (parent?.fileStatusId) {
        fsId = parent.fileStatusId;
      }
      return [
        ...prev,
        { name: node.name, path: node.path, fileStatusId: fsId, nodeType: node.nodeType },
      ];
    });
  }, []);

  const navigateTo = useCallback((index: number) => {
    setNavStack((prev) => (index < 0 ? [] : prev.slice(0, index + 1)));
  }, []);

  const handleRowClick = useCallback(
    (node: TreeNodeResponse) => {
      if (isFoldersMode) return;
      const isDirectory = node.nodeType === 'directory';
      const canDrill = isDirectory || node.hasChildren === true;
      if (!canDrill) return;
      // In search mode, drilling clears the search so the breadcrumb makes sense.
      if (isSearching && onClearSearch) onClearSearch();
      navigateInto(node);
    },
    [isFoldersMode, isSearching, navigateInto, onClearSearch],
  );

  const handleNavigateToNode = useCallback(
    (node: TreeNodeResponse) => {
      if (node.nodeType === 'subdocument' && node.sourceFile) {
        // Land the user *inside* the containing file so they can see siblings.
        const sfSegments = node.sourceFile.searchablePath.split('/').filter(Boolean);
        const fileName = sfSegments.pop();
        const stack: NavEntry[] = sfSegments.map((name, i) => ({
          name,
          path: sfSegments.slice(0, i + 1).join('/'),
          fileStatusId: null,
          nodeType: 'directory' as const,
        }));
        if (fileName) {
          stack.push({
            name: fileName,
            path: node.sourceFile.searchablePath,
            fileStatusId: node.fileStatusId ?? node.sourceFile.id,
            nodeType: 'file',
          });
        }
        setNavStack(stack);
      } else if (node.nodeType === 'directory') {
        const segments = node.path.split('/').filter(Boolean);
        const stack: NavEntry[] = segments.map((name, i) => ({
          name,
          path: segments.slice(0, i + 1).join('/'),
          fileStatusId: null,
          nodeType: 'directory' as const,
        }));
        setNavStack(stack);
      } else {
        const segments = node.path.split('/').filter(Boolean);
        segments.pop();
        if (segments.length === 0) {
          setNavStack([]);
        } else {
          const stack: NavEntry[] = segments.map((name, i) => ({
            name,
            path: segments.slice(0, i + 1).join('/'),
            fileStatusId: null,
            nodeType: 'directory' as const,
          }));
          setNavStack(stack);
        }
      }
      onClearSearch?.();
    },
    [onClearSearch],
  );

  const getVisibleSelection = useCallback(() => {
    const fileUrls: string[] = [];
    const folderUrls: string[] = [];
    for (const node of displayItems) {
      if (node.nodeType === 'directory') {
        folderUrls.push(nodeAbsolutePath(node, contextSourceFilePath));
      } else {
        fileUrls.push(nodePickUrl(node, toUrl, contextSourceFilePath));
      }
    }
    return { fileUrls, folderUrls };
  }, [displayItems, toUrl, contextSourceFilePath]);

  useImperativeHandle(
    ref,
    () => ({ navigateToNode: handleNavigateToNode, getVisibleSelection }),
    [handleNavigateToNode, getVisibleSelection],
  );

  // Show the breadcrumb whenever there's a nav stack, even in search mode —
  // otherwise drilling into a folder under a category filter would strand the
  // user with no way to navigate further (path-links only exist *on rows*).
  // Folders mode has its own dedicated header (count + action buttons).
  const showBreadcrumb = !isFoldersMode && (!isSearching || navStack.length > 0);
  const showResultsHeader = !isFoldersMode && isSearching;

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {isFoldersMode && (
        <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-[14px] text-black/60 border-b border-brand-blue-light/30">
          <span className="truncate">
            {loading
              ? t('chat.evidence.tree.loadingFolders')
              : t(foldersTotal === 1
                ? 'chat.evidence.tree.folderCount'
                : 'chat.evidence.tree.folderCountPlural', {
                  count: folderNodes.length,
                  total: foldersTotal > folderNodes.length ? ` ${t('chat.evidence.tree.ofTotal', { total: foldersTotal })}` : '',
                  matching: foldersSearch
                    ? ` ${t('chat.evidence.tree.matching', { search: foldersSearch })}`
                    : '',
                })}
          </span>
          {headerActions && (
            <div className="flex items-center gap-1 flex-shrink-0 ml-auto">{headerActions}</div>
          )}
        </div>
      )}
      {showBreadcrumb && (
        <div className="flex items-center gap-2 border-b border-brand-blue-light/30">
          <div className="flex-1 min-w-0">
            <Breadcrumb
              navStack={navStack}
              breadcrumbStartIndex={caseBrowseRootCrumbDepth}
              onNavigate={navigateTo}
              onHome={() => navigateTo(caseBrowseRootCrumbDepth > 0 ? caseBrowseRootCrumbDepth - 1 : -1)}
              totalCount={browseTotal}
              hideBottomBorder
            />
          </div>
          {/* Pin Select All / Clear All to the breadcrumb row whenever it
            * exists so they stay at the same vertical position as the user
            * toggles between browse and search/filter modes. The
            * results-header below only owns its own controls (Search all). */}
          {headerActions && (
            <div className="flex items-center gap-1 px-2 flex-shrink-0">{headerActions}</div>
          )}
        </div>
      )}
      {showResultsHeader && (
        <div className="flex items-center gap-2 px-3 pt-2 pb-1 text-[14px] text-black/60">
          {/* Left cluster: results summary + escape-hatch back to a
            * cell-wide search. Keeping ``Search all`` next to the "N results
            * in <path>" label anchors it to what it modifies. */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="truncate">
              {searchQueryHook.isLoading
                ? t('chat.evidence.tree.searching')
                : t(displayItems.length === 1
                  ? 'chat.evidence.tree.resultCount'
                  : 'chat.evidence.tree.resultCountPlural', { count: displayItems.length })}
            </span>
            {navPathQuery && (
              <Button
                type="button"
                size="sm"
                style="text"
                color="accent"
                background="default"
                onClick={() => navigateTo(-1)}
                className="flex-shrink-0"
                title={t('chat.evidence.tree.searchAllHelp')}
              >
                {t('chat.evidence.tree.searchAll')}
              </Button>
            )}
          </div>
          {/* Fallback slot for the action buttons when there is no
            * breadcrumb (cell-wide search from root). */}
          {headerActions && !showBreadcrumb && (
            <div className="flex items-center gap-1 flex-shrink-0">{headerActions}</div>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 px-1 py-1">
        {loading && displayItems.length === 0 ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-pulse text-sm text-black/70">{t('chat.evidence.tree.loading')}</div>
          </div>
        ) : error ? (
          <div className="px-3 py-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
            {error.message}
          </div>
        ) : showEmpty ? (
          <div className="text-sm text-black/60 text-center py-6">
            {isFoldersMode
              ? foldersSearch
                ? t('chat.evidence.tree.noFoldersMatch')
                : t('chat.evidence.tree.noFolders')
              : isSearching
                ? t('chat.evidence.tree.noResults')
                : t('chat.evidence.tree.emptyFolder')}
          </div>
        ) : (
          <div ref={scrollRef} className="h-full min-h-0 overflow-y-auto custom-scrollbar3 pr-1">
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((vi) => {
                const isExtra = vi.index >= displayItems.length;
                return (
                  <div
                    key={vi.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: vi.size,
                      transform: `translateY(${vi.start}px)`,
                    }}
                  >
                    {isExtra ? (
                      <div className="flex items-center justify-center gap-2 h-full px-3">
                        {showLoadingMore ? (
                          <div className="animate-pulse text-xs text-black/60">{t('chat.evidence.tree.loadingMore')}</div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            style="outline"
                            color="subdued"
                            background="default"
                            onClick={() => loadMore()}
                            className="px-3 py-1 text-xs"
                          >
                            {t('chat.evidence.tree.loadMore')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <TreeRow
                        node={displayItems[vi.index]}
                        toUrl={toUrl}
                        contextSourceFilePath={contextSourceFilePath}
                        pickedPaths={pickedPaths}
                        excludedPaths={excludedPaths}
                        pickedFolderPaths={pickedFolderPaths}
                        excludedFolderPaths={excludedFolderPaths}
                        isSearchResult={isSearching}
                        onRowClick={handleRowClick}
                        onTogglePick={onTogglePick}
                        onTogglePickFolder={onTogglePickFolder}
                        onNavigateToParent={handleNavigateToNode}
                        renderRowPreview={renderRowPreview}
                        renderRowNameSuffix={renderRowNameSuffix}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function Breadcrumb({
  navStack,
  breadcrumbStartIndex = 0,
  onNavigate,
  onHome,
  totalCount,
  hideBottomBorder = false,
}: {
  navStack: NavEntry[];
  breadcrumbStartIndex?: number;
  onNavigate: (index: number) => void;
  onHome: () => void;
  totalCount: number;
  hideBottomBorder?: boolean;
}) {
  const t = useTimePilotT();
  const start = Math.min(Math.max(0, breadcrumbStartIndex), navStack.length);
  const visibleStack = navStack.slice(start);
  const atHome = navStack.length <= start;
  return (
    <div
      className={`flex items-center gap-1 px-3 pt-2 pb-1 text-[14px] overflow-x-auto scrollbar-hide ${
        hideBottomBorder ? '' : 'border-b border-brand-blue-light/30'
      }`}
    >
      <Button
        type="button"
        size="sm"
        style="text"
        color="subdued"
        background="default"
        onClick={onHome}
        leadingIcon={<HouseIcon size={16} weight="regular" />}
        className={`px-1.5 py-0.5 flex-shrink-0 ${
          atHome
            ? 'text-black font-medium'
            : 'text-black/60 hover:text-black hover:bg-brand-blue-light/15'
        }`}
      >
        {t('caseFiles.root')}
      </Button>
      {visibleStack.map((entry, i) => {
        const fullIndex = start + i;
        return (
        <div key={`${entry.path}-${fullIndex}`} className="flex items-center gap-1 flex-shrink-0">
          <CaretRightIcon size={12} className="text-black/40" />
          <Button
            type="button"
            size="sm"
            style="text"
            color="subdued"
            background="default"
            onClick={() => onNavigate(fullIndex)}
            className={`px-1.5 py-0.5 truncate max-w-[180px] ${
              fullIndex === navStack.length - 1
                ? 'text-black font-medium'
                : 'text-black/60 hover:text-black hover:bg-brand-blue-light/15'
            }`}
          >
            {entry.name}
          </Button>
        </div>
        );
      })}
      {totalCount > 0 && navStack.length === 0 && (
        <span className="ml-auto pl-2 text-[11px] text-black/50 flex-shrink-0">
          {t(totalCount === 1 ? 'chat.evidence.tree.itemCount' : 'chat.evidence.tree.itemCountPlural', { count: totalCount })}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TreeRow
// ---------------------------------------------------------------------------

interface TreeRowProps {
  node: TreeNodeResponse;
  toUrl: (path: string) => string;
  contextSourceFilePath?: string;
  pickedPaths: Set<string>;
  excludedPaths?: Set<string>;
  pickedFolderPaths?: Set<string>;
  excludedFolderPaths?: Set<string>;
  isSearchResult: boolean;
  onRowClick: (node: TreeNodeResponse) => void;
  onTogglePick: (url: string, node: TreeNodeResponse) => void;
  onTogglePickFolder?: (url: string, node: TreeNodeResponse) => void;
  onNavigateToParent: (node: TreeNodeResponse) => void;
  renderRowPreview?: EvidenceLockerTreeProps['renderRowPreview'];
  renderRowNameSuffix?: EvidenceLockerTreeProps['renderRowNameSuffix'];
}

function TreeRow({
  node,
  toUrl,
  contextSourceFilePath,
  pickedPaths,
  excludedPaths,
  pickedFolderPaths,
  excludedFolderPaths,
  isSearchResult,
  onRowClick,
  onTogglePick,
  onTogglePickFolder,
  onNavigateToParent,
  renderRowPreview,
  renderRowNameSuffix,
}: TreeRowProps) {
  const t = useTimePilotT();
  const { extension, pageCount } = nodeMeta(node);
  const isDirectory = node.nodeType === 'directory';
  const hasChildren = node.hasChildren === true;
  // ``canDrill`` controls the cursor + keyboard affordance for navigating
  // into a row. Both directories and files (mbox / archive / multi-page PDF)
  // can be drilled into when they have children. In folders mode we
  // synthesize directories with ``hasChildren: false`` to disable
  // navigation, so a single ``hasChildren`` check covers all cases.
  const canDrill = hasChildren;
  // ``pickUrl`` flows into scope/selection state. Files land in
  // ``selectedEvidence``; folders land in ``selectedScopes`` via
  // ``onTogglePickFolder``. The two intentionally use different URL shapes:
  //
  // - Files run through ``toUrl`` (= ``toProvenanceLookupPath``) so the URL
  //   carries the canonical ``data/bulk/`` prefix that downstream surfaces
  //   need: the ``availableEvidence`` index used by ``renderRowPreview``,
  //   ``SourceCard`` lookups, ``/files/file-info``, ``/files/file-status``,
  //   and ``/api/proxy``.
  // - Folders stay as the raw absolute searchable path because they're only
  //   ever used as filter values (assistant-service ``searchable_path Glob``
  //   matching, chip display) — they never roundtrip through provenance
  //   lookups, so the prefix would only need to be stripped again.
  //
  // For top-level rows ``node.path`` is already absolute; for rows inside a
  // file, ``contextSourceFilePath`` (the source file the user drilled into)
  // provides the missing prefix so subdocuments and virtual directories
  // resolve to the full ``{source.searchable_path}/{relative path}`` shape
  // that tai-processing writes into Turbopuffer.
  //
  // The wire boundary in ``chat.tsx`` runs ``toSearchablePath`` over both
  // arrays before sending them to the assistant-service, so the backend
  // always receives raw searchable paths regardless of the in-memory shape.
  //
  // ``previewUrl`` flows into the SourceCard preview. For subdocuments this
  // is ``fileInfo.subdocument.fileUrl`` (the actual processed ``s3://…`` URI)
  // so the card shows the real storage location, not the lookup-style path.
  const pickUrl = isDirectory
    ? nodeAbsolutePath(node, contextSourceFilePath)
    : nodePickUrl(node, toUrl, contextSourceFilePath);
  const previewUrl = isDirectory ? '' : nodePreviewUrl(node, pickUrl);
  const folderPickable = isDirectory && !!onTogglePickFolder;
  const isPicked = isDirectory
    ? folderPickable && (pickedFolderPaths?.has(pickUrl) ?? false)
    : pickedPaths.has(pickUrl);
  const isExcluded = isDirectory
    ? folderPickable && (excludedFolderPaths?.has(pickUrl) ?? false)
    : (excludedPaths?.has(pickUrl) ?? false);

  const Icon = isDirectory ? FolderOpenIcon : iconForFileNode(node);
  const mediaType = (extension ?? node.name.split('.').pop() ?? '').toLowerCase();

  const rowBg = isPicked
    ? 'bg-[#99C4F6]/30 border-[#2d8dfe]'
    : isExcluded
      ? 'bg-red-100 border-red-500'
      : 'border-transparent hover:bg-brand-blue-light/10 hover:border-brand-blue-light/40';

  return (
    <div
      role={canDrill ? 'button' : undefined}
      tabIndex={canDrill ? 0 : -1}
      onClick={() => onRowClick(node)}
      onKeyDown={
        canDrill
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick(node);
              }
            }
          : undefined
      }
      className={`group flex items-center gap-2 px-2 h-full rounded-md border ${rowBg} ${
        canDrill ? 'cursor-pointer' : ''
      }`}
    >
      <Icon
        size={22}
        weight="regular"
        className={isDirectory ? 'text-brand-blue flex-shrink-0' : 'text-black/75 flex-shrink-0'}
      />
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={`text-sm text-black/90 ${PATH_SEGMENT_TRUNCATE_CLASSNAME}`}>{node.name}</span>
        {!isDirectory && renderRowNameSuffix?.({ url: previewUrl, node, mediaType, pageCount: pageCount ?? null })}
      </div>

      {isSearchResult ? (
        <Button
          type="button"
          size="sm"
          style="text"
          color="subdued"
          background="default"
          onClick={(e) => {
            e.stopPropagation();
            onNavigateToParent(node);
          }}
          className={`flex-1 min-w-0 text-left text-[11px] text-black/45 hover:text-brand-blue cursor-pointer ${PATH_SEGMENT_TRUNCATE_CLASSNAME}`}
          title={node.path}
        >
          {node.path}
        </Button>
      ) : (
        <div className="flex-1" />
      )}

      {isDirectory ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasChildren && (
            <CaretRightIcon
              size={18}
              weight="regular"
              className="text-black/40 group-hover:text-black/70 flex-shrink-0"
            />
          )}
          {folderPickable && (
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="subdued"
              background="default"
              onClick={(e) => {
                e.stopPropagation();
                onTogglePickFolder?.(pickUrl, node);
              }}
              className={`w-7 h-7 rounded-md border flex items-center justify-center ${
                isPicked
                  ? 'bg-brand-blue border-brand-blue text-white'
                  : isExcluded
                    ? 'bg-red-500 border-red-500 text-white'
                    : 'border-black/30 hover:border-brand-blue hover:bg-brand-blue-light/20'
              }`}
              aria-label={isPicked ? 'Remove folder from scope' : 'Add folder to scope'}
              title={isPicked ? 'Remove folder from scope' : 'Add folder to scope'}
            >
              {isPicked ? (
                <CheckIcon weight="bold" />
              ) : isExcluded ? (
                <XIcon weight="bold" />
              ) : (
                <PlusIcon weight="regular" className="text-black/40 group-hover:text-brand-blue" />
              )}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasChildren && (
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="subdued"
              background="default"
              onClick={(e) => {
                e.stopPropagation();
                onRowClick(node);
              }}
              className="flex items-center justify-center w-7 h-7 rounded-md text-black/65 border border-transparent hover:border-brand-blue/40 hover:bg-brand-blue-light/20"
              aria-label={t('chat.evidence.tree.browseContents')}
              title={t('chat.evidence.tree.browseContents')}
            >
              <CaretRightIcon weight="regular" className="text-black/60" />
            </Button>
          )}
          {renderRowPreview?.({
            url: previewUrl,
            node,
            mediaType,
            pageCount: pageCount ?? null,
          })}
          <Button
            type="button"
            iconOnly
            size="sm"
            style="text"
            color="subdued"
            background="default"
            onClick={(e) => {
              e.stopPropagation();
              onTogglePick(pickUrl, node);
            }}
            className={`w-7 h-7 rounded-md border flex items-center justify-center ${
              isPicked
                ? 'bg-brand-blue border-brand-blue text-white'
                : isExcluded
                  ? 'bg-red-500 border-red-500 text-white'
                  : 'border-black/30 hover:border-brand-blue hover:bg-brand-blue-light/20'
            }`}
            aria-label={isPicked ? 'Remove from selection' : 'Add to selection'}
            title={isPicked ? 'Remove from selection' : 'Add to selection'}
          >
            {isPicked ? (
              <CheckIcon weight="bold" />
            ) : isExcluded ? (
              <XIcon weight="bold" />
            ) : (
              <PlusIcon weight="regular" className="text-black/40 group-hover:text-brand-blue" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
